import { useI18n } from 'vue-i18n';

/**
 * Locale-aware formatters for chart axes and labels. The single source of
 * truth is the active vue-i18n locale, so the currency symbol and date labels
 * follow the user's configured language (e.g. CLP / "$" for es-CL, BRL / "R$"
 * for pt-BR). Use instead of hardcoding "R$" or feeding raw ISO dates to
 * ApexCharts (which would render month names in English).
 */
export function useChartFormat() {
  const { d, locale, getNumberFormat } = useI18n();

  /** Active-locale currency symbol, e.g. "R$" (pt-BR), "$" (es-CL). */
  function currencySymbol(): string {
    const nf = getNumberFormat(locale.value) as Record<string, { currency?: string }> | undefined;
    const code = nf?.currency?.currency ?? 'BRL';
    const part = new Intl.NumberFormat(locale.value, { style: 'currency', currency: code })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part?.value ?? code;
  }

  /** "2025-03" → localized month+year label (falls back to the raw value). */
  function monthLabel(yyyymm: string): string {
    const [y, m] = (yyyymm ?? '').split('-').map(Number);
    if (!y || !m) return yyyymm ?? '';
    return d(new Date(y, m - 1, 1), 'monthShort');
  }

  /** "2025-03-15" → localized day+month label (falls back to the raw value). */
  function dayLabel(iso: string): string {
    const [y, m, day] = (iso ?? '').split('-').map(Number);
    if (!y || !m || !day) return iso ?? '';
    return d(new Date(y, m - 1, day), 'dayShort');
  }

  return { currencySymbol, monthLabel, dayLabel };
}
